import type { Config } from 'final-form';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { InputField } from '../../components/FinalForm/InputField';
import { SelectField } from '../../components/FinalForm/SelectField';
import { SubmitButton } from '../../components/FinalForm/SubmitButton';
import { TextAreaField } from '../../components/FinalForm/TextAreaField';
import { useWebsite } from '../../hooks/useWebsite';
import type { ValidationFnType } from '../../utils/validation';
import { composeValidations, isEmail, validationMessages } from '../../utils/validation';
import type { ContactFormPayload } from '../api/contact';
import { postContactForm } from '../api/contact';

// Requis STRICT (non vide) — l'util partagé `validateRequired` ne teste que `!== undefined`,
// donc laisse passer `''` (champ vidé après saisie) ; l'endpoint re-valide de toute façon,
// mais on veut un hint inline plutôt qu'une erreur générique serveur. Local au formulaire pour
// ne pas modifier le comportement de l'util partagé (utilisé par d'autres formulaires).
const validateNotEmpty: ValidationFnType<string> = value =>
  value && value.trim() ? null : validationMessages.required;

export const messages = defineMessages({
  nameLabel: { defaultMessage: 'Your name', id: 'vlKhIl' },
  emailLabel: { defaultMessage: 'Your email', id: 'nONnTw' },
  reasonLabel: { defaultMessage: 'Reason for your message', id: 'aBlcqG' },
  reasonBecomeWriter: { defaultMessage: 'Join the editorial team (volunteer)', id: 'iWSz1q' },
  reasonSuggestTopic: { defaultMessage: 'Suggest a topic', id: 'Ua8Ucb' },
  reasonOther: { defaultMessage: 'Other', id: '/VnDMl' },
  messageLabel: { defaultMessage: 'Your message', id: 't3WXkE' },
  send: { defaultMessage: 'Send', id: '9WRlF4' },
  success: {
    defaultMessage: 'Thank you! Your message has been sent, we will get back to you soon.',
    id: 'TGIUBD',
  },
  error: {
    defaultMessage: 'Your message could not be sent. Please try again in a moment.',
    id: 'QS9Mtd',
  },
});

// Valeurs du menu « motif » — préfixées dans le corps du message envoyé au staff (l'endpoint
// n'a pas de champ dédié). L'ordre suit le récit D43 (appel à contribution en tête).
const REASONS = [
  { value: 'become_writer', label: messages.reasonBecomeWriter },
  { value: 'suggest_topic', label: messages.reasonSuggestTopic },
  { value: 'other', label: messages.reasonOther },
] as const;

type ContactFormValues = {
  nom: string;
  email: string;
  reason: string;
  message: string;
  // Honeypot : champ leurre masqué aux humains (CSS + aria-hidden). Un bot qui remplit
  // automatiquement tous les champs le renseigne → on abandonne silencieusement.
  website_url?: string;
};

export const ContactForm = () => {
  const { formatMessage } = useIntl();
  const website = useWebsite();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const onSubmit: Config<ContactFormValues>['onSubmit'] = async values => {
    // Honeypot rempli → soumission silencieusement ignorée (on simule un succès pour ne pas
    // renseigner le bot, mais aucune requête ne part).
    if (values.website_url) {
      setSent(true);
      return;
    }
    setError(false);

    const reasonLabel = REASONS.find(r => r.value === values.reason)?.label ?? messages.reasonOther;
    // L'endpoint n'a ni champ « motif » ni champ « tenant » → on les injecte dans le message
    // pour que le staff (boîte partagée contact@semiologic.fr, D45) sache d'où vient la demande
    // et à quel titre.
    const composedMessage = `[${formatMessage(reasonLabel)}] — ${website.name}\n\n${
      values.message
    }`;

    const payload: ContactFormPayload = {
      nom: values.nom,
      email: values.email,
      message: composedMessage,
      product: 'graphdebate',
    };

    try {
      await postContactForm(payload);
      setSent(true);
    } catch {
      setError(true);
    }
  };

  if (sent) {
    return (
      <p className="msg text-center" role="status">
        <FormattedMessage {...messages.success} />
      </p>
    );
  }

  return (
    <Form<ContactFormValues>
      onSubmit={onSubmit}
      initialValues={{ reason: REASONS[0].value }}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
          <InputField
            name="nom"
            required
            validate={validateNotEmpty}
            placeholder={formatMessage(messages.nameLabel)}
            aria-label={formatMessage(messages.nameLabel)}
          />
          <InputField
            name="email"
            type="email"
            required
            validate={composeValidations([validateNotEmpty, isEmail])}
            placeholder={formatMessage(messages.emailLabel)}
            aria-label={formatMessage(messages.emailLabel)}
          />
          <SelectField
            name="reason"
            aria-label={formatMessage(messages.reasonLabel)}
            options={REASONS.map(r => ({ value: r.value, label: r.label }))}
          />
          <TextAreaField
            name="message"
            required
            validate={validateNotEmpty}
            placeholder={formatMessage(messages.messageLabel)}
            aria-label={formatMessage(messages.messageLabel)}
          />

          {/* Honeypot : hors flux visuel, jamais montré ni annoncé aux lecteurs d'écran. */}
          <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
            <InputField name="website_url" tabIndex={-1} autoComplete="off" />
          </div>

          {error && (
            <p className="text-failure text-center" role="alert">
              <FormattedMessage {...messages.error} />
            </p>
          )}

          <div className="flex justify-center">
            <SubmitButton type="submit">
              <FormattedMessage {...messages.send} />
            </SubmitButton>
          </div>
        </form>
      )}
    />
  );
};
